// aiDiagramTools — Pure-core för AI diagram-authoring. Funktioner här
// är sida-effekt-fria så de kan unit-testas utan DOM / LLM / Supabase.
// Tool-wrappers i aiToolWrappersExtended.ts exponerar dessa för AI:n.
//
// Kontrakt:
// - Alla returnvärden är nya objekt (input mutas aldrig).
// - Grid-snap 20px tillämpas på all input-koordinat.
// - UUID:er genereras via crypto.randomUUID() — inga id-kollisioner.
// - Batch-operationer accepterar N inputs i ett call → AI slipper
//   round-trip per entity.

import type {
  BlockGroup,
  BlockType,
  Connector,
  CrossSheetConnector,
  MultiSheetWorkflowState,
  PortSide,
  WorkflowBlock,
  WorkflowSheet,
} from '@/types/workflow';

// ── Konstanter ──────────────────────────────────────────────────────
export const GRID_SIZE = 20;
export const DEFAULT_BLOCK_WIDTH = 200;
export const DEFAULT_BLOCK_HEIGHT = 80;
export const COLUMN_STRIDE = 320;
export const ROW_STRIDE = 160;
export const VALID_PORTS: readonly PortSide[] = [
  'top', 'top-right', 'right', 'bottom-right',
  'bottom', 'bottom-left', 'left', 'top-left',
];

// ── Grid / layout ───────────────────────────────────────────────────
/**
 * Snap:a en koordinat till närmaste multipel av GRID_SIZE (20).
 * -27 → -20, 13 → 20, 42 → 40 (standard rounding).
 */
export function snapToGrid(n: number): number {
  return Math.round(n / GRID_SIZE) * GRID_SIZE;
}

export function snapPoint(p: { x: number; y: number }): { x: number; y: number } {
  return { x: snapToGrid(p.x), y: snapToGrid(p.y) };
}

/**
 * Generera en default-position för det n:te blocket i ett sheet.
 * Fyller en rad åt gången (3 kolumner default) från startposition.
 */
export function gridPosition(
  index: number,
  opts: { columns?: number; startX?: number; startY?: number } = {},
): { x: number; y: number } {
  const columns = opts.columns ?? 3;
  const startX = opts.startX ?? -1000;
  const startY = opts.startY ?? -400;
  const col = index % columns;
  const row = Math.floor(index / columns);
  return {
    x: snapToGrid(startX + col * COLUMN_STRIDE),
    y: snapToGrid(startY + row * ROW_STRIDE),
  };
}

// ── UUID (testbar) ──────────────────────────────────────────────────
/**
 * Insprutbar uuid-generator för deterministiska tester. Om inte satt
 * används crypto.randomUUID.
 */
let uuidGen: () => string = () => crypto.randomUUID();

export function setUuidGenerator(fn: () => string): void {
  uuidGen = fn;
}

export function resetUuidGenerator(): void {
  uuidGen = () => crypto.randomUUID();
}

// ── Diagram planning (no mutations) ─────────────────────────────────
export interface DiagramPlanSheetDraft {
  index: number;
  name: string;
  color: string;
  icon?: string;
  description?: string;
  estimatedBlocks: number;
}

export interface DiagramPlan {
  goal: string;
  sheetCount: number;
  style: 'pipeline' | 'layered' | 'module-graph' | 'hub-spoke' | 'swimlane' | 'architecture';
  sheets: DiagramPlanSheetDraft[];
}

const DEFAULT_SHEET_COLORS = [
  '#1a2e5a', '#4f46e5', '#8b5cf6', '#3ecf8e', '#f97316', '#dc2626',
  '#eab308', '#06b6d4', '#22c55e', '#ec4899',
];

/**
 * Föreslå en sheet-uppdelning för ett diagram-goal. Ren funktion — kan
 * köras utan LLM som fallback. LLM:en kan också kalla denna för att
 * få en start-plan.
 */
export function planDiagram(
  goal: string,
  opts: { sheetCount?: number; style?: DiagramPlan['style'] } = {},
): DiagramPlan {
  const sheetCount = Math.max(1, Math.min(10, opts.sheetCount ?? 3));
  const style = opts.style ?? inferStyleFromGoal(goal);
  const sheets: DiagramPlanSheetDraft[] = Array.from({ length: sheetCount }, (_, i) => ({
    index: i,
    name: defaultSheetName(i, style),
    color: DEFAULT_SHEET_COLORS[i % DEFAULT_SHEET_COLORS.length],
    estimatedBlocks: style === 'module-graph' ? 10 : 8,
  }));
  return { goal, sheetCount, style, sheets };
}

function inferStyleFromGoal(goal: string): DiagramPlan['style'] {
  const g = goal.toLowerCase();
  if (/arkitekt|architecture/.test(g)) return 'architecture';
  if (/modul|folder|repo-graph/.test(g)) return 'module-graph';
  if (/pipeline|kedja|linear|sequence/.test(g)) return 'pipeline';
  if (/lager|stack|layered/.test(g)) return 'layered';
  if (/swim|lane/.test(g)) return 'swimlane';
  if (/hub|spoke|nav/.test(g)) return 'hub-spoke';
  return 'architecture';
}

function defaultSheetName(index: number, style: DiagramPlan['style']): string {
  const prefix = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'][index] ?? `(${index + 1})`;
  switch (style) {
    case 'architecture': return `${prefix} Layer ${index + 1}`;
    case 'module-graph': return `${prefix} Module ${index + 1}`;
    case 'pipeline': return `${prefix} Stage ${index + 1}`;
    case 'layered': return `${prefix} Tier ${index + 1}`;
    case 'swimlane': return `${prefix} Lane ${index + 1}`;
    case 'hub-spoke': return index === 0 ? `${prefix} Hub` : `${prefix} Spoke ${index}`;
  }
}

// ── Sheet creation ──────────────────────────────────────────────────
export interface CreateSheetDraftInput {
  name: string;
  meta?: WorkflowSheet['meta'];
  id?: string;
}

export function createSheetDraft(input: CreateSheetDraftInput): WorkflowSheet {
  return {
    id: input.id ?? uuidGen(),
    name: input.name,
    blocks: [],
    connectors: [],
    groups: [],
    ...(input.meta ? { meta: input.meta } : {}),
  };
}

// ── Batch block creation ────────────────────────────────────────────
export interface BlockDraftInput {
  /** Client-local reference så AI:n kan referera blocket i connectors. */
  ref: string;
  type: BlockType;
  x: number;
  y: number;
  config: WorkflowBlock['config'];
  color?: string;
}

export interface BatchCreateBlocksResult {
  blocks: WorkflowBlock[];
  /** Map från client-ref → tilldelad UUID. */
  idMap: Record<string, string>;
}

/**
 * Skapa N blocks i ett pass. Grid-snap:ar koordinater, tilldelar UUID:er
 * och returnerar både den färdiga blocks-arrayen och en map så connector-
 * steget kan refera blocks via client-refs istället för att behöva veta
 * UUID:erna i förväg.
 */
export function batchCreateBlocks(
  inputs: BlockDraftInput[],
  opts: { defaultColor?: string } = {},
): BatchCreateBlocksResult {
  const defaultColor = opts.defaultColor ?? '#6366f1';
  const blocks: WorkflowBlock[] = [];
  const idMap: Record<string, string> = {};
  const now = Date.now();
  for (const input of inputs) {
    const id = uuidGen();
    idMap[input.ref] = id;
    blocks.push({
      id,
      type: input.type,
      x: snapToGrid(input.x),
      y: snapToGrid(input.y),
      config: { ...input.config },
      color: input.color ?? defaultColor,
      mtime: now,
    });
  }
  return { blocks, idMap };
}

// ── Batch connector creation ────────────────────────────────────────
export interface ConnectorDraftInput {
  /** Client-refs (från BatchCreateBlocksResult.idMap) eller faktiska block-ids. */
  fromRef: string;
  toRef: string;
  fromPort?: PortSide;
  toPort?: PortSide;
  label?: string;
}

export interface BatchCreateConnectorsResult {
  connectors: Connector[];
  /** Inputs som förkastades + orsak. */
  rejected: Array<{ input: ConnectorDraftInput; reason: string }>;
}

function isValidPort(p: PortSide | undefined): boolean {
  return p === undefined || VALID_PORTS.includes(p);
}

function resolveRef(ref: string, idMap: Record<string, string>, existingIds: Set<string>): string | null {
  if (idMap[ref]) return idMap[ref];
  if (existingIds.has(ref)) return ref;
  return null;
}

/**
 * Skapa N connectors i ett pass. De-dupe:ar exact-duplicates (same endpoints +
 * ports), validerar port-enum, resolver ref→id via idMap.
 */
export function batchCreateConnectors(
  inputs: ConnectorDraftInput[],
  idMap: Record<string, string>,
  opts: { existingBlockIds?: string[]; existingConnectors?: Connector[] } = {},
): BatchCreateConnectorsResult {
  const existingIds = new Set(opts.existingBlockIds ?? []);
  const existingConnectors = opts.existingConnectors ?? [];
  const connectors: Connector[] = [];
  const rejected: BatchCreateConnectorsResult['rejected'] = [];
  const seen = new Set<string>();
  const now = Date.now();

  const dedupeKey = (c: { fromId: string; toId: string; fromPort?: PortSide; toPort?: PortSide }) => {
    const a = `${c.fromId}:${c.fromPort ?? 'right'}`;
    const b = `${c.toId}:${c.toPort ?? 'left'}`;
    return a < b ? `${a}→${b}` : `${b}→${a}`;
  };

  for (const c of existingConnectors) {
    seen.add(dedupeKey(c));
  }

  for (const input of inputs) {
    if (!isValidPort(input.fromPort) || !isValidPort(input.toPort)) {
      rejected.push({ input, reason: `invalid-port: ${input.fromPort}/${input.toPort}` });
      continue;
    }
    const fromId = resolveRef(input.fromRef, idMap, existingIds);
    const toId = resolveRef(input.toRef, idMap, existingIds);
    if (!fromId || !toId) {
      rejected.push({ input, reason: `unresolved-ref: ${input.fromRef}/${input.toRef}` });
      continue;
    }
    const fromPort = input.fromPort ?? 'right';
    const toPort = input.toPort ?? 'left';
    const key = dedupeKey({ fromId, toId, fromPort, toPort });
    if (seen.has(key)) {
      rejected.push({ input, reason: 'duplicate' });
      continue;
    }
    seen.add(key);
    connectors.push({
      id: uuidGen(),
      fromId, toId, fromPort, toPort,
      ...(input.label ? { label: input.label } : {}),
      mtime: now,
    });
  }
  return { connectors, rejected };
}

// ── Cross-sheet link ────────────────────────────────────────────────
export interface CrossSheetLinkInput {
  fromSheetIndex: number;
  toSheetIndex: number;
  fromBlockId: string;
  toBlockId: string;
  fromPort?: PortSide;
  toPort?: PortSide;
  label?: string;
}

export type CrossSheetLinkResult =
  | { ok: true; connector: CrossSheetConnector }
  | { ok: false; error: 'non-adjacent' | 'invalid-port' | 'same-sheet' };

/**
 * Skapa en cross-sheet-connector. Reglerna (från workflow.ts) är:
 * - |from - to| === 1 (endast grannsheets)
 * - from !== to
 * - port-enum giltig
 */
export function createCrossSheetLink(input: CrossSheetLinkInput): CrossSheetLinkResult {
  if (input.fromSheetIndex === input.toSheetIndex) {
    return { ok: false, error: 'same-sheet' };
  }
  // Adjacency constraint lifted in Ship 7 (Caveat 5A). The two
  // sheet indices just need to be distinct + within range.
  if (!isValidPort(input.fromPort) || !isValidPort(input.toPort)) {
    return { ok: false, error: 'invalid-port' };
  }
  const connector: CrossSheetConnector = {
    id: uuidGen(),
    fromSheetIndex: input.fromSheetIndex,
    toSheetIndex: input.toSheetIndex,
    fromBlockId: input.fromBlockId,
    toBlockId: input.toBlockId,
    ...(input.fromPort ? { fromPort: input.fromPort } : {}),
    ...(input.toPort ? { toPort: input.toPort } : {}),
    ...(input.label ? { label: input.label } : {}),
  };
  return { ok: true, connector };
}

// ── Group creation ──────────────────────────────────────────────────
export interface CreateGroupInput {
  blockIds: string[];
  color: string;
  name?: string;
  collapsed?: boolean;
  collapsedX?: number;
  collapsedY?: number;
  parentGroupId?: string;
}

export function createGroupDraft(input: CreateGroupInput): BlockGroup {
  const group: BlockGroup = {
    id: uuidGen(),
    blockIds: [...input.blockIds],
    color: input.color,
    mtime: Date.now(),
  };
  if (input.name) group.name = input.name;
  if (input.collapsed) {
    group.collapsed = true;
    group.collapsedX = input.collapsedX !== undefined ? snapToGrid(input.collapsedX) : 0;
    group.collapsedY = input.collapsedY !== undefined ? snapToGrid(input.collapsedY) : 0;
  }
  if (input.parentGroupId) group.parentGroupId = input.parentGroupId;
  return group;
}

// ── Validation ──────────────────────────────────────────────────────
export interface DiagramViolation {
  kind:
    | 'off-grid'
    | 'invalid-port'
    | 'duplicate-group-membership'
    | 'non-adjacent-cross-sheet'
    | 'dangling-connector'
    | 'dangling-cross-sheet'
    | 'layer-missing-coords'
    | 'duplicate-id';
  message: string;
  /** Kontext: blockId/connectorId/groupId/sheetIndex. */
  ref?: string;
}

export interface ValidateDiagramReport {
  ok: boolean;
  violations: DiagramViolation[];
  counts: {
    sheets: number;
    blocks: number;
    connectors: number;
    crossSheetConnectors: number;
  };
}

/**
 * Kör alla invariant-checks mot ett MultiSheetWorkflowState. Returnerar
 * samlade violations så AI:n kan iterera tills allt är grönt innan
 * approveAiStage.
 */
export function validateDiagram(state: MultiSheetWorkflowState): ValidateDiagramReport {
  const violations: DiagramViolation[] = [];
  const seenIds = new Set<string>();
  const blockMemberships = new Map<string, string>(); // blockId → groupId

  let totalBlocks = 0;
  let totalConnectors = 0;

  for (let i = 0; i < state.sheets.length; i++) {
    const sheet = state.sheets[i];
    if (seenIds.has(sheet.id)) {
      violations.push({ kind: 'duplicate-id', ref: sheet.id, message: `Sheet-id "${sheet.id}" används på flera sheets.` });
    } else {
      seenIds.add(sheet.id);
    }

    const blockIdsInSheet = new Set<string>();
    for (const b of sheet.blocks) {
      totalBlocks++;
      if (seenIds.has(b.id)) {
        violations.push({ kind: 'duplicate-id', ref: b.id, message: `Block-id "${b.id}" används flera ggr.` });
      } else {
        seenIds.add(b.id);
      }
      blockIdsInSheet.add(b.id);
      if (b.x % GRID_SIZE !== 0 || b.y % GRID_SIZE !== 0) {
        violations.push({
          kind: 'off-grid', ref: b.id,
          message: `Block "${b.id}" har koordinater (${b.x}, ${b.y}) — måste vara multiplar av ${GRID_SIZE}.`,
        });
      }
    }

    for (const c of sheet.connectors) {
      totalConnectors++;
      if (!isValidPort(c.fromPort) || !isValidPort(c.toPort)) {
        violations.push({
          kind: 'invalid-port', ref: c.id,
          message: `Connector "${c.id}" har ogiltig port: ${c.fromPort}/${c.toPort}.`,
        });
      }
      if (!blockIdsInSheet.has(c.fromId) && !hasGroupId(sheet.groups, c.fromId)) {
        violations.push({
          kind: 'dangling-connector', ref: c.id,
          message: `Connector "${c.id}" fromId="${c.fromId}" finns inte på sheet ${i}.`,
        });
      }
      if (!blockIdsInSheet.has(c.toId) && !hasGroupId(sheet.groups, c.toId)) {
        violations.push({
          kind: 'dangling-connector', ref: c.id,
          message: `Connector "${c.id}" toId="${c.toId}" finns inte på sheet ${i}.`,
        });
      }
    }

    for (const g of sheet.groups) {
      if (seenIds.has(g.id)) {
        violations.push({ kind: 'duplicate-id', ref: g.id, message: `Group-id "${g.id}" används flera ggr.` });
      } else {
        seenIds.add(g.id);
      }
      if (g.collapsed) {
        if (g.collapsedX === undefined || g.collapsedY === undefined) {
          violations.push({
            kind: 'layer-missing-coords', ref: g.id,
            message: `Layer "${g.id}" är collapsed men saknar collapsedX/collapsedY.`,
          });
        }
      }
      for (const bid of g.blockIds) {
        const existing = blockMemberships.get(bid);
        if (existing && existing !== g.id) {
          violations.push({
            kind: 'duplicate-group-membership', ref: bid,
            message: `Block "${bid}" är medlem i flera groups (${existing}, ${g.id}).`,
          });
        } else {
          blockMemberships.set(bid, g.id);
        }
      }
    }
  }

  const crossSheet = state.crossSheetConnectors ?? [];
  for (const cs of crossSheet) {
    if (cs.fromSheetIndex === cs.toSheetIndex) {
      violations.push({
        kind: 'non-adjacent-cross-sheet', ref: cs.id,
        message: `Cross-sheet "${cs.id}" har fromSheetIndex === toSheetIndex.`,
      });
    // Adjacency check removed in Ship 7 (Caveat 5A) — connectors may
    // link any two distinct sheets. Only same-sheet self-loops fail.
    }
    const fromSheet = state.sheets[cs.fromSheetIndex];
    const toSheet = state.sheets[cs.toSheetIndex];
    if (!fromSheet || !toSheet) {
      violations.push({
        kind: 'dangling-cross-sheet', ref: cs.id,
        message: `Cross-sheet "${cs.id}" refererar okänt sheet-index.`,
      });
      continue;
    }
    if (!fromSheet.blocks.some(b => b.id === cs.fromBlockId)) {
      violations.push({
        kind: 'dangling-cross-sheet', ref: cs.id,
        message: `Cross-sheet "${cs.id}" fromBlockId="${cs.fromBlockId}" finns inte på sheet ${cs.fromSheetIndex}.`,
      });
    }
    if (!toSheet.blocks.some(b => b.id === cs.toBlockId)) {
      violations.push({
        kind: 'dangling-cross-sheet', ref: cs.id,
        message: `Cross-sheet "${cs.id}" toBlockId="${cs.toBlockId}" finns inte på sheet ${cs.toSheetIndex}.`,
      });
    }
  }

  return {
    ok: violations.length === 0,
    violations,
    counts: {
      sheets: state.sheets.length,
      blocks: totalBlocks,
      connectors: totalConnectors,
      crossSheetConnectors: crossSheet.length,
    },
  };
}

function hasGroupId(groups: BlockGroup[], id: string): boolean {
  return groups.some(g => g.id === id);
}
